import { useQuery } from '@tanstack/react-query';
import { lazy, Suspense, useEffect, useMemo, useState, type JSX } from 'react';
import { useAuthStore } from '../auth/authStore';
import { makeApi } from '../../lib/api';
import { EditorLayout } from '../layout/EditorLayout';
import { Topbar } from '../layout/Topbar';
import {
  SpaceTree,
  type StoryTreeNode,
} from '../space-tree/SpaceTree';
// Hot-path + shell-critical surfaces stay eager — they're on the
// default Stories view or the chrome around every view.
import { StoryEditor } from './StoryEditor';
import { PreviewPane } from '../preview/PreviewPane';
import { AssetLibrary } from '../assets/AssetLibrary';
import { FirstSpaceWizard } from '../onboarding/FirstSpaceWizard';
import { BulkToolsPanel } from './BulkToolsPanel';
import { CreateStoryDialog } from './CreateStoryDialog';
import type { StoryListEntry } from '@peacock/sdk-js';

// Secondary tab panels are code-split (2026-06-08) — most editor
// sessions live on Stories/Assets and never open Forms/Shop/Events/etc.
// Lazy-loading keeps ~13 panels' worth of code (+ their dialog forms)
// out of the initial bundle; each loads its own chunk on first tab
// click. Named exports are unwrapped to the `default` shape lazy()
// expects.
const AuditLog = lazy(() => import('../audit/AuditLog').then((m) => ({ default: m.AuditLog })));
const AudiencesPanel = lazy(() => import('../audiences/AudiencesPanel').then((m) => ({ default: m.AudiencesPanel })));
const AnalyticsPanel = lazy(() => import('../analytics/AnalyticsPanel').then((m) => ({ default: m.AnalyticsPanel })));
const DatasourcesPanel = lazy(() => import('../datasources/DatasourcesPanel').then((m) => ({ default: m.DatasourcesPanel })));
const BillingPanel = lazy(() => import('../billing/BillingPanel').then((m) => ({ default: m.BillingPanel })));
const PitchInviteesPanel = lazy(() => import('../pitch/PitchInviteesPanel').then((m) => ({ default: m.PitchInviteesPanel })));
const PluginsPanel = lazy(() => import('../plugins/PluginsPanel').then((m) => ({ default: m.PluginsPanel })));
const BlueprintsPanel = lazy(() => import('../blueprints/BlueprintsPanel').then((m) => ({ default: m.BlueprintsPanel })));
const FormsPanel = lazy(() => import('../forms/FormsPanel').then((m) => ({ default: m.FormsPanel })));
const EventsPanel = lazy(() => import('../events/EventsPanel').then((m) => ({ default: m.EventsPanel })));
const ShopPanel = lazy(() => import('../shop/ShopPanel').then((m) => ({ default: m.ShopPanel })));
const NewsletterPanel = lazy(() => import('../newsletter/NewsletterPanel').then((m) => ({ default: m.NewsletterPanel })));
const GalleriesPanel = lazy(() => import('../galleries/GalleriesPanel').then((m) => ({ default: m.GalleriesPanel })));

/** Shared fallback while a lazy panel chunk loads. */
function PanelLoading(): JSX.Element {
  return (
    <div className="flex h-full items-center justify-center p-8 text-sm text-zinc-500" role="status">
      Lade Bereich…
    </div>
  );
}

type AdminView =
  | 'stories'
  | 'assets'
  | 'audit'
  | 'audiences'
  | 'analytics'
  | 'datasources'
  | 'billing'
  | 'plugins'
  | 'investoren'
  | 'blueprints'
  | 'forms'
  | 'events'
  | 'shop'
  | 'newsletter'
  | 'galleries';

/**
 * Phase 3 increment B — the editor entry point once a token is in
 * the auth store.
 *
 * Wires:
 *  - `useAuthStore` for token + Space slug
 *  - TanStack Query for the Story tree (`api.listStories`)
 *  - The three layout components from increment A (Topbar, SpaceTree,
 *    EditorLayout)
 *  - Local UI state for the currently selected Story path
 *
 * The middle column is a placeholder until the Plate.js editor lands
 * in increment C. The right column is empty (no preview yet — wires
 * up in increment D).
 */
export default function EditorShell(): JSX.Element {
  const token = useAuthStore((s) => s.token);
  const spaceSlug = useAuthStore((s) => s.spaceSlug);
  const signOut = useAuthStore((s) => s.signOut);
  const activeLocale = useAuthStore((s) => s.activeLocale);
  const setActiveLocale = useAuthStore((s) => s.setActiveLocale);

  const api = useMemo(() => (token === null ? null : makeApi(token)), [token]);

  const [selectedPath, setSelectedPath] = useState<string | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false);
  const [livePreviewContent, setLivePreviewContent] = useState<
    Record<string, unknown> | null
  >(null);
  const [view, setView] = useState<AdminView>('stories');
  const [showCreateStory, setShowCreateStory] = useState<boolean>(false);

  // Mobile sidebar-drawer state. The drawer auto-closes whenever the
  // tab changes so a mobile editor doesn't end up with the drawer
  // permanently covering the main content (the tab buttons live
  // INSIDE the drawer; without this effect, tapping a tab would
  // toggle the view but leave the drawer covering the new panel).
  const [drawerOpen, setDrawerOpen] = useState<boolean>(false);
  useEffect(() => {
    setDrawerOpen(false);
  }, [view, selectedPath]);

  // Preview URL is per-Space config (Phase 7 will surface this in the
  // admin UI). For now we read from `VITE_PEACOCK_PREVIEW_BASE_URL`
  // env, with `null` meaning "no preview wired" → PreviewPane shows
  // a friendly placeholder.
  const previewBaseUrl = import.meta.env.VITE_PEACOCK_PREVIEW_BASE_URL ?? null;
  const previewUrl =
    previewBaseUrl !== null && selectedPath !== null
      ? `${String(previewBaseUrl).replace(/\/+$/, '')}${selectedPath}?peacock-preview=1`
      : null;

  // Fetch all Stories in the Space. Phase 3 B uses a single
  // un-paginated query for the tree because the realistic upper
  // bound is "a few hundred Stories" for the typical webhoch
  // customer; if/when we hit 10k+ Stories per Space the tree should
  // switch to per-folder lazy-loading via `parent_id`. The endpoint
  // already supports it.
  // Phase 3 G — locales for the language switcher. Keys on spaceSlug
  // only (locales don't change per session) and stays cached for
  // 5min — the admin's locale list rarely changes during a session.
  const localesQuery = useQuery({
    enabled: api !== null && spaceSlug !== null,
    queryKey: ['locales', spaceSlug],
    queryFn: async () => {
      if (api === null || spaceSlug === null) {
        throw new Error('queryFn ran without prerequisites');
      }
      const result = await api.listLocales(spaceSlug);
      if (result.status !== 'ok') {
        throw new Error(`listLocales failed: ${result.status}`);
      }
      return result.data;
    },
    staleTime: 5 * 60_000,
  });

  const storiesQuery = useQuery({
    enabled: api !== null && spaceSlug !== null,
    queryKey: ['stories', spaceSlug, activeLocale],
    queryFn: async () => {
      if (api === null || spaceSlug === null) {
        throw new Error('queryFn ran without api/spaceSlug — should be guarded by enabled');
      }
      const result = await api.listStories(spaceSlug, {
        perPage: 100,
        lang: activeLocale,
      });
      if (result.status !== 'ok') {
        throw new Error(
          `listStories failed: ${result.status}` +
            (result.status === 'forbidden' ? ` (${result.code})` : ''),
        );
      }
      return result.data;
    },
    staleTime: 30_000,
  });

  const treeNodes = useMemo<StoryTreeNode[]>(() => {
    const flat = storiesQuery.data?.data ?? [];
    return buildTree(flat);
  }, [storiesQuery.data]);

  // Derive the selected Story's UUID from the selected path. Without
  // this the StoryEditor would need a path → UUID round-trip; the
  // tree already has both fields adjacent.
  const selectedStoryUuid = useMemo<string | null>(() => {
    if (selectedPath === null) return null;
    const flat = storiesQuery.data?.data ?? [];
    const match = flat.find((s) => s.full_path === selectedPath);
    return match?.uuid ?? null;
  }, [selectedPath, storiesQuery.data]);

  return (
    <>
    <EditorLayout
      drawerOpen={drawerOpen}
      onDrawerChange={setDrawerOpen}
      topbar={
        <Topbar
          spaceName={spaceSlug}
          spaceLocaleCode={activeLocale}
          availableLocales={
            localesQuery.data?.data.map((l) => ({ code: l.code, label: l.label })) ?? []
          }
          onLocaleChange={setActiveLocale}
          hasUnsavedChanges={hasUnsavedChanges}
          onLogoutClick={signOut}
        />
      }
      sidebar={
        <div className="flex flex-col gap-3">
          {/* Horizontal scroll strip + 44px tap-target floor
              (responsive-audit-fixer 2026-05-30 MEDIUM). The 9 tabs
              previously overflowed the 280px sidebar at 375vw and
              each button rendered at 32px — below Apple HIG. */}
          <nav
            className="-mx-3 overflow-x-auto px-3"
            style={{
              maskImage: 'linear-gradient(to right, transparent 0, black 12px, black calc(100% - 24px), transparent 100%)',
              WebkitMaskImage: 'linear-gradient(to right, transparent 0, black 12px, black calc(100% - 24px), transparent 100%)',
            }}
            aria-label="Editor-Bereich auswählen"
          >
            <div className="flex min-w-max gap-1 rounded-md bg-zinc-200 p-1 text-xs">
              {(['stories', 'assets', 'blueprints', 'audiences', 'forms', 'events', 'shop', 'newsletter', 'galleries', 'datasources', 'analytics', 'audit', 'billing', 'plugins', 'investoren'] as const).map((v) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => setView(v)}
                  data-testid={
                    v === 'audiences' ? 'audiences-tab'
                    : v === 'analytics' ? 'analytics-tab'
                    : v === 'datasources' ? 'datasources-tab'
                    : v === 'billing' ? 'billing-tab'
                    : v === 'plugins' ? 'plugins-tab'
                    : v === 'investoren' ? 'investoren-tab'
                    : undefined
                  }
                  className={
                    'inline-flex min-h-11 shrink-0 items-center whitespace-nowrap rounded px-3 py-2 capitalize transition ' +
                    (view === v
                      ? 'bg-white font-medium text-zinc-900 shadow-sm'
                      : 'text-zinc-600 hover:text-zinc-900')
                  }
                >
                  {v === 'audit' ? 'AI Audit' : v}
                </button>
              ))}
            </div>
          </nav>

          {view === 'stories' ? (
            <>
              <header className="flex items-center justify-between px-2 pt-1">
                <h2 className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
                  Stories
                </h2>
                <div className="flex items-center gap-2">
                  <span className="font-mono text-xs text-zinc-400">
                    {storiesQuery.data?.meta.total ?? '—'}
                  </span>
                  <button
                    type="button"
                    onClick={() => setShowCreateStory(true)}
                    aria-label="Neue Story anlegen"
                    title="Neue Story anlegen"
                    data-testid="new-story-button"
                    className="flex h-5 w-5 items-center justify-center rounded bg-zinc-900 text-white hover:bg-zinc-700 text-xs leading-none"
                  >
                    +
                  </button>
                </div>
              </header>
              {storiesQuery.isPending ? (
                <p className="px-2 text-xs text-zinc-500">Lade Stories…</p>
              ) : storiesQuery.isError ? (
                <p
                  role="alert"
                  className="rounded border border-red-200 bg-red-50 px-2 py-1 text-xs text-red-900"
                >
                  {(storiesQuery.error as Error).message}
                </p>
              ) : treeNodes.length === 0 ? (
                <div className="rounded border border-dashed border-zinc-300 bg-zinc-50 px-3 py-4 text-center text-xs text-zinc-600">
                  <p className="mb-2">Noch keine Stories.</p>
                  <button
                    type="button"
                    onClick={() => setShowCreateStory(true)}
                    className="rounded bg-zinc-900 px-2 py-1 text-xs font-medium text-white hover:bg-zinc-700"
                  >
                    + Erste Story anlegen
                  </button>
                </div>
              ) : (
                <>
                  <SpaceTree
                    nodes={treeNodes}
                    selectedPath={selectedPath}
                    onSelect={setSelectedPath}
                  />
                  <BulkToolsPanel />
                </>
              )}
            </>
          ) : view === 'audiences' ? (
            <p className="px-2 text-xs text-zinc-500">
              Verwalte Audience-Segments im Hauptbereich →
            </p>
          ) : view === 'datasources' ? (
            <p className="px-2 text-xs text-zinc-500">
              Externe APIs spiegeln. Stories referenzieren Einträge per{' '}
              <code className="rounded bg-zinc-100 px-1 font-mono text-[10px]">$ds</code> →
            </p>
          ) : view === 'billing' ? (
            <p className="px-2 text-xs text-zinc-500">
              Plan, Limits, Nutzung. Stripe-verwaltet wenn aktiviert →
            </p>
          ) : view === 'analytics' ? (
            <div className="px-2 text-xs text-zinc-500">
              <p className="mb-2">
                Story-Analytics oder Space-Übersicht. Story-Auswahl
                wirkt sich auf die Detail-Ansicht aus.
              </p>
              <button
                type="button"
                onClick={() => setView('stories')}
                className="rounded border border-zinc-300 px-2 py-1 hover:bg-zinc-100"
              >
                ← Story wählen
              </button>
            </div>
          ) : view === 'plugins' ? (
            <p className="px-2 text-xs text-zinc-500">
              Plugins erweitern Peacock um Webhook-Subscriber und
              Custom Field-Types. Im Hauptbereich verwalten →
            </p>
          ) : view === 'investoren' ? (
            <p className="px-2 text-xs text-zinc-500">
              Investoren-Demos die über die hidden Pitch-Seite
              self-provisioned wurden. Hauptbereich →
            </p>
          ) : (
            <p className="px-2 text-xs text-zinc-500">
              Im Hauptbereich verwalten →
            </p>
          )}
        </div>
      }
      main={
        view === 'stories' ? (
          // First-time onboarding wizard: show ONLY when the Space has
          // zero Stories AND the user hasn't manually selected one yet
          // (so the wizard doesn't pop back over the editor mid-edit).
          // Once any Story exists, the wizard self-dismisses (it returns
          // an empty fragment) and StoryEditor takes over.
          selectedStoryUuid === null && (storiesQuery.data?.data?.length ?? 0) === 0 ? (
            <FirstSpaceWizard
              onStoryCreated={(uuid) => {
                // The wizard's createStory mutation already invalidated
                // ['stories', spaceSlug]. Re-fetch + select the new
                // Story so the StoryEditor opens with it focused.
                void storiesQuery.refetch().then((res) => {
                  const flat = res.data?.data ?? [];
                  const created = flat.find((s) => s.uuid === uuid);
                  if (created) {
                    setSelectedPath(created.full_path);
                  }
                });
              }}
            />
          ) : (
            <StoryEditor
              storyUuid={selectedStoryUuid}
              onDirtyChange={setHasUnsavedChanges}
              onContentChange={setLivePreviewContent}
            />
          )
        ) : view === 'assets' ? (
          <AssetLibrary />
        ) : (
          // All remaining tabs are lazy-loaded — one Suspense boundary
          // covers the whole set so a tab switch shows the loading
          // fallback while its chunk fetches. (Code-split 2026-06-08.)
          <Suspense fallback={<PanelLoading />}>
            {view === 'audiences' ? (
              <AudiencesPanel />
            ) : view === 'datasources' ? (
              <DatasourcesPanel />
            ) : view === 'billing' ? (
              <BillingPanel />
            ) : view === 'analytics' ? (
              <AnalyticsPanel storyUuid={selectedStoryUuid} />
            ) : view === 'plugins' ? (
              <PluginsPanel />
            ) : view === 'investoren' ? (
              <PitchInviteesPanel />
            ) : view === 'blueprints' ? (
              <BlueprintsPanel />
            ) : view === 'forms' ? (
              <FormsPanel />
            ) : view === 'events' ? (
              <EventsPanel />
            ) : view === 'shop' ? (
              <ShopPanel />
            ) : view === 'newsletter' ? (
              <NewsletterPanel />
            ) : view === 'galleries' ? (
              <GalleriesPanel />
            ) : (
              <AuditLog />
            )}
          </Suspense>
        )
      }
      preview={
        view === 'stories' ? (
          <PreviewPane
            previewUrl={previewUrl}
            content={livePreviewContent}
            storyUuid={selectedStoryUuid}
            onBlockClick={(path) => {
              // eslint-disable-next-line no-console
              console.log('peacock preview click:', path);
            }}
          />
        ) : undefined
      }
    />
    {showCreateStory ? (
      <CreateStoryDialog
        existingStories={storiesQuery.data?.data ?? []}
        defaultParentId={null}
        onClose={() => setShowCreateStory(false)}
        onCreated={(story) => {
          setShowCreateStory(false);
          setSelectedPath(story.full_path);
        }}
      />
    ) : null}
    </>
  );
}

/**
 * Folds the flat `[Story, …]` list returned by
 * `/v1/spaces/{slug}/stories` into a parent-children tree the
 * SpaceTree component renders.
 *
 * Two-pass build:
 *  1. Index every Story by `id` so we can attach children to parents
 *     in O(1).
 *  2. Walk the list once, push each Story under its parent (or to the
 *     root list if it has no parent), sorted by `full_path` so the
 *     UI is stable run-to-run.
 *
 * Orphan handling: if a Story's `parent_id` doesn't resolve to any
 * Story in the current page of results (e.g. pagination cut between
 * a parent and its child, or the parent was deleted under our feet),
 * we attach the orphan to the root list rather than dropping it. The
 * editor showing a "floating" Story is preferable to silently hiding
 * it from the tree.
 */
function buildTree(flat: StoryListEntry[]): StoryTreeNode[] {
  // Stable index so we can spot orphans (parent_id pointing to an id
  // not in the current page).
  const known = new Set<number>(flat.map((entry) => entry.id));

  const nodesById = new Map<number, StoryTreeNode>();
  for (const entry of flat) {
    nodesById.set(entry.id, {
      uuid: entry.uuid,
      slug: entry.slug || '/',
      full_path: entry.full_path,
      status: (entry.status as StoryTreeNode['status']) ?? 'draft',
      children: [],
    });
  }

  const roots: StoryTreeNode[] = [];
  for (const entry of flat) {
    const node = nodesById.get(entry.id);
    if (node === undefined) {
      // Shouldn't happen — we just populated nodesById from the same
      // list — but the type system can't prove it, and a missing node
      // here would silently drop a Story from the tree.
      continue;
    }
    const parentId = entry.parent_id;
    if (parentId !== null && known.has(parentId)) {
      const parent = nodesById.get(parentId);
      parent?.children?.push(node);
    } else {
      roots.push(node);
    }
  }

  // Sort siblings by `full_path` so the rendered order is
  // deterministic across reloads. Stable Array.prototype.sort
  // (ES2019+) means original-order ties survive.
  const sortBySlug = (nodes: StoryTreeNode[]): void => {
    nodes.sort((a, b) => a.full_path.localeCompare(b.full_path));
    for (const node of nodes) {
      if (node.children !== undefined && node.children.length > 0) {
        sortBySlug(node.children);
      }
    }
  };
  sortBySlug(roots);

  return roots;
}
